/**
 * Leading glyph for Connected apps catalog rows (Merge + Nango).
 * Brand logo inside FramedIcon, falling back to a Lucide glyph when the
 * connector has no artwork or the logo host is unreachable (on-prem airgap).
 */

import { FramedIcon } from '@heidi/design-system/framed-icon';
import { useState } from 'react';
import {
  getConnectedAppLogoUrl,
  getConnectedAppLucideIcon,
  normalizeConnectedAppIconKey,
} from './connected-app-icon-utils';

/** `size` is injected by FramedIcon from the frame size. */
function ConnectedAppLogo({
  src,
  size,
  onError,
}: {
  src: string;
  size?: number;
  onError: () => void;
}) {
  return (
    // eslint-disable-next-line @next/next/no-img-element -- remote third-party logo, not a Next-optimisable asset
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      onError={onError}
      className="object-contain"
    />
  );
}

export function ConnectedAppIcon({
  id,
  label,
  testId,
}: {
  id: string;
  /** Accessible name when the icon is the sole label (usually omit — row text labels). */
  label?: string;
  testId?: string;
}) {
  // Tracked by URL, not a boolean, so a reused instance rendering a different
  // connector isn't stuck on the fallback from the previous one's failure.
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | undefined>(undefined);
  const Icon = getConnectedAppLucideIcon(id);
  const logoUrl = getConnectedAppLogoUrl(id);
  const showLogo = logoUrl !== undefined && logoUrl !== failedLogoUrl;

  return (
    <FramedIcon
      size="md"
      color="neutral"
      icon={
        showLogo ? (
          <ConnectedAppLogo src={logoUrl} onError={() => setFailedLogoUrl(logoUrl)} />
        ) : (
          <Icon />
        )
      }
      className="shrink-0"
      aria-label={label}
      data-testid={testId ?? `connected-app-icon-${normalizeConnectedAppIconKey(id)}`}
    />
  );
}
